import type { OnboardingProgress, OnboardingConfig, UserPreferences } from '../../shared/types/onboarding';

export class OnboardingService {
  private static readonly PROGRESS_KEY = 'invirtiendo_onboarding_progress';
  private static readonly CONFIG_KEY = 'invirtiendo_onboarding_config';
  private static readonly PREFERENCES_KEY = 'invirtiendo_user_preferences';

  // Obtener progreso del onboarding
  static getProgress(userId: string): OnboardingProgress | null {
    try {
      const stored = localStorage.getItem(this.PROGRESS_KEY);
      if (!stored) return null;

      const allProgress: OnboardingProgress[] = JSON.parse(stored);
      const userProgress = allProgress.find(p => p.userId === userId);

      if (!userProgress) return null;

      return {
        ...userProgress,
        startedAt: new Date(userProgress.startedAt),
        completedAt: userProgress.completedAt ? new Date(userProgress.completedAt) : undefined
      };
    } catch (error) {
      console.error('Error obteniendo progreso de onboarding:', error);
      return null;
    }
  }

  // Guardar progreso del onboarding
  static saveProgress(progress: OnboardingProgress): void {
    try {
      const stored = localStorage.getItem(this.PROGRESS_KEY);
      const allProgress: OnboardingProgress[] = stored ? JSON.parse(stored) : [];

      const existingIndex = allProgress.findIndex(p => p.userId === progress.userId);
      
      if (existingIndex >= 0) {
        allProgress[existingIndex] = progress;
      } else {
        allProgress.push(progress);
      }

      localStorage.setItem(this.PROGRESS_KEY, JSON.stringify(allProgress));
      
      console.log('Progreso de onboarding guardado:', {
        userId: progress.userId,
        currentStep: progress.currentStep,
        completedSteps: progress.completedSteps.length
      });
    } catch (error) {
      console.error('Error guardando progreso de onboarding:', error);
    }
  }

  // Inicializar onboarding para nuevo usuario
  static initializeOnboarding(userId: string): OnboardingProgress {
    const initialProgress: OnboardingProgress = {
      userId,
      currentStep: 0,
      completedSteps: [],
      isCompleted: false,
      startedAt: new Date(),
      skippedSteps: []
    };

    this.saveProgress(initialProgress);
    return initialProgress;
  }

  // Marcar paso como completado
  static completeStep(userId: string, stepId: string): OnboardingProgress | null {
    try {
      const progress = this.getProgress(userId);
      if (!progress) return null;

      if (!progress.completedSteps.includes(stepId)) {
        progress.completedSteps.push(stepId);
      }

      // Avanzar al siguiente paso si no está ya más adelante
      if (progress.currentStep < progress.completedSteps.length) {
        progress.currentStep = progress.completedSteps.length;
      }

      this.saveProgress(progress);
      return progress;
    } catch (error) {
      console.error('Error completando paso de onboarding:', error);
      return null;
    }
  }

  // Saltar paso
  static skipStep(userId: string, stepId: string): OnboardingProgress | null {
    try {
      const progress = this.getProgress(userId);
      if (!progress) return null;

      if (!progress.skippedSteps.includes(stepId)) {
        progress.skippedSteps.push(stepId);
      }

      progress.currentStep++;
      this.saveProgress(progress);
      return progress;
    } catch (error) {
      console.error('Error saltando paso de onboarding:', error);
      return null;
    }
  }

  // Completar onboarding
  static completeOnboarding(userId: string): OnboardingProgress | null {
    try {
      const progress = this.getProgress(userId);
      if (!progress) return null;

      progress.isCompleted = true;
      progress.completedAt = new Date();

      this.saveProgress(progress);
      
      console.log('Onboarding completado:', {
        userId,
        totalSteps: progress.completedSteps.length,
        skippedSteps: progress.skippedSteps.length
      });

      return progress;
    } catch (error) {
      console.error('Error completando onboarding:', error);
      return null;
    }
  }

  // Configuración del onboarding
  static getConfig(): OnboardingConfig {
    try {
      const stored = localStorage.getItem(this.CONFIG_KEY);
      if (!stored) {
        return this.getDefaultConfig();
      }

      return JSON.parse(stored);
    } catch (error) {
      console.error('Error obteniendo configuración de onboarding:', error);
      return this.getDefaultConfig();
    }
  }

  static saveConfig(config: OnboardingConfig): void {
    try {
      localStorage.setItem(this.CONFIG_KEY, JSON.stringify(config));
    } catch (error) {
      console.error('Error guardando configuración de onboarding:', error);
    }
  }

  private static getDefaultConfig(): OnboardingConfig {
    return {
      showWelcome: true,
      autoAdvance: false,
      allowSkip: true,
      showProgress: true,
      highlightElements: true
    };
  }

  // Preferencias del usuario
  static getUserPreferences(userId: string): UserPreferences | null {
    try {
      const stored = localStorage.getItem(this.PREFERENCES_KEY);
      if (!stored) return null;

      const allPreferences: UserPreferences[] = JSON.parse(stored);
      return allPreferences.find(p => p.userId === userId) || null;
    } catch (error) {
      console.error('Error obteniendo preferencias de usuario:', error);
      return null;
    }
  }

  static saveUserPreferences(preferences: UserPreferences): void {
    try {
      const stored = localStorage.getItem(this.PREFERENCES_KEY);
      const allPreferences: UserPreferences[] = stored ? JSON.parse(stored) : [];

      const existingIndex = allPreferences.findIndex(p => p.userId === preferences.userId);
      
      if (existingIndex >= 0) {
        allPreferences[existingIndex] = preferences;
      } else {
        allPreferences.push(preferences);
      }

      localStorage.setItem(this.PREFERENCES_KEY, JSON.stringify(allPreferences));
      
      console.log('Preferencias de usuario guardadas:', {
        userId: preferences.userId,
        categories: preferences.categories.length
      });
    } catch (error) {
      console.error('Error guardando preferencias de usuario:', error);
    }
  }

  // Crear preferencias por defecto
  static createDefaultPreferences(userId: string): UserPreferences {
    const defaultPreferences: UserPreferences = {
      userId,
      categories: ['Alimentación', 'Transporte', 'Vivienda', 'Entretenimiento'],
      currency: 'ARS',
      language: 'es',
      notifications: {
        budget: true,
        weekly: false,
        monthly: true
      },
      dashboardLayout: 'simple'
    };

    this.saveUserPreferences(defaultPreferences);
    return defaultPreferences;
  }

  // Verificar si el usuario ha completado el onboarding
  static hasCompletedOnboarding(userId: string): boolean {
    const progress = this.getProgress(userId);
    return progress?.isCompleted || false;
  }

  // Reiniciar onboarding
  static resetOnboarding(userId: string): void {
    try {
      const stored = localStorage.getItem(this.PROGRESS_KEY);
      if (!stored) return;

      let allProgress: OnboardingProgress[] = JSON.parse(stored);
      allProgress = allProgress.filter(p => p.userId !== userId);

      if (allProgress.length === 0) {
        localStorage.removeItem(this.PROGRESS_KEY);
      } else {
        localStorage.setItem(this.PROGRESS_KEY, JSON.stringify(allProgress));
      }

      console.log('Onboarding reiniciado:', { userId });
    } catch (error) {
      console.error('Error reiniciando onboarding:', error);
    }
  }
}